import React, { useRef, useState, useEffect } from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const Dropdown = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  title,
  error,
  required,
  className = '',
  maxWidth = 'max-w-xs',
  renderOption = null, // Custom render function for options
  renderSelected = null, // Custom render function for selected value
  disabled = false
}) => {
  // Find the selected option object
  const selectedOption = options.find(opt => opt.value === value);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [dropdownPlacement, setDropdownPlacement] = useState('bottom');

  // Update button width and position when it changes
  useEffect(() => {
    if (buttonRef.current && containerRef.current) {
      const updateMeasurements = () => {
        if (!buttonRef.current || !containerRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        setButtonWidth(rect.width);
        setContainerWidth(containerRect.width);

        // Check if showing dropdown below would go out of viewport
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const dropdownHeight = Math.min(options.length * 40, 240); // Estimate dropdown height (40px per item, max 240px)

        if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
          // Not enough space below, but enough space above
          setDropdownPlacement('top');
          setButtonPosition({
            top: rect.top,
            left: rect.left
          });
        } else {
          // Default: show below
          setDropdownPlacement('bottom');
          setButtonPosition({
            top: rect.bottom,
            left: rect.left
          });
        }

        // Handle horizontal alignment as well
        const viewportWidth = window.innerWidth;
        if (rect.left + rect.width > viewportWidth) {
          // Adjust left position to ensure dropdown stays within viewport
          setButtonPosition(prev => ({
            ...prev,
            left: Math.max(10, viewportWidth - rect.width - 10)
          }));
        }
      };

      updateMeasurements();

      // Add event listeners for resize and scroll
      window.addEventListener('resize', updateMeasurements);
      window.addEventListener('scroll', updateMeasurements, true);

      return () => {
        window.removeEventListener('resize', updateMeasurements);
        window.removeEventListener('scroll', updateMeasurements, true);
      };
    }
  }, [buttonRef.current, containerRef.current, options.length]);

  // Default render functions
  const defaultRenderOption = (option, active, selected) => (
    <div className="flex items-center w-full py-1">
      <div className="flex items-center w-full max-w-full overflow-hidden">
        <span className="block max-w-full whitespace-nowrap text-ellipsis overflow-hidden">
          {option.label}
        </span>
      </div>
    </div>
  );

  const defaultRenderSelected = (option) => (
    <div className="flex items-center h-full w-full max-w-full">
      <span className={`block max-w-full overflow-hidden text-ellipsis whitespace-nowrap ${!option || !option.value ? 'text-gray-500' : 'text-gray-900'}`}>
        {option ? option.label : placeholder}
      </span>
    </div>
  );

  // Use custom render functions if provided, otherwise use defaults
  const renderOptionFn = renderOption || defaultRenderOption;
  const renderSelectedFn = renderSelected || defaultRenderSelected;

  // Get the class for the button based on whether it's disabled or has an error
  const getButtonClasses = () => {
    let classes = 'relative w-full rounded-md border-0 ';

    // Add ring color based on error or disabled state
    if (disabled) {
      classes += 'ring-1 ring-inset ring-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed ';
    } else if (error) {
      classes += 'ring-1 ring-inset ring-red-300 bg-white cursor-default ';
    } else {
      classes += 'ring-1 ring-inset ring-gray-300 bg-white cursor-default ';
    }

    // Common classes for all states
    classes += 'py-2 pl-3 pr-10 text-left ';

    // Focus states (only when not disabled)
    if (!disabled) {
      classes += 'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-teal focus:border-brand-teal ';
    }

    // Sizing and styling
    classes += 'h-10 flex items-center text-base overflow-hidden outline-none ';

    // Additional class for single line
    if (className.includes('single-line-dropdown')) {
      classes += 'whitespace-nowrap ';
    }

    return classes;
  };

  return (
    <div ref={containerRef} className={`flex flex-col ${maxWidth} ${className}`}>
      {title && (
        <label className={`block text-label font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'} mb-2`}>
          {title} {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative w-full">
        <Listbox value={value} onChange={onChange} disabled={disabled}>
          {({ open }) => (
            <div className="relative">
              <Listbox.Button
                ref={buttonRef}
                className={getButtonClasses()}
              >
                {renderSelectedFn(selectedOption)}
                <span className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 ${disabled ? 'text-gray-300' : 'text-gray-400'}`}>
                  <ChevronDownIcon
                    className="h-5 w-5"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              {/* Position dropdown manually to follow button position */}
              {!disabled && (
                <Listbox.Options
                  className="fixed z-50 max-h-60 overflow-auto rounded-md bg-white py-1 text-gray-500 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-base"
                  style={{
                    width: buttonWidth > 0 ? `${Math.max(buttonWidth, 250)}px` : 'auto',
                    maxWidth: containerWidth > 0 ? `${Math.max(containerWidth, 250)}px` : '100%',
                    top: dropdownPlacement === 'top'
                      ? `${buttonPosition.top - 5 - (open ? 240 : 0)}px` // Position above button
                      : `${buttonPosition.top + 5}px`, // Position below button
                    left: `${buttonPosition.left}px`,
                  }}
                >
                  {options.map((option, index) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active, selected }) =>
                        `relative cursor-default select-none py-2 pl-3 pr-9 overflow-hidden ${selected
                          ? 'bg-brand-teal text-white'
                          : active
                            ? 'bg-brand-form-bg text-gray-900'
                            : 'text-gray-500'
                        }`
                      }
                    >
                      {({ active, selected }) => renderOptionFn(option, active, selected)}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              )}
            </div>
          )}
        </Listbox>
      </div>
      {error && !disabled && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Dropdown; 